import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { CheckCircle2, Clock, DollarSign, Package, ClipboardList } from '../lib/icons';
import { shadows } from '../lib/styles';

interface HoneyDonePlanWidgetProps {
  planData: {
    approach?: string;
    approach_details?: {
      in_house_work?: boolean;
    };
    estimated_complexity?: string;
    estimated_timeline?: string;
    estimated_cost_range?: string;
    estimated_cost?: string;
    plan_summary?: string;
    task_breakdown?: string[];
    materials_needed?: string[];
    next_steps?: string;
  };
}

export const HoneyDonePlanWidget: React.FC<HoneyDonePlanWidgetProps> = ({
  planData,
}) => {
  const taskBreakdown = planData.task_breakdown || [];
  const materialsNeeded = planData.materials_needed || [];
  const timeline = planData.estimated_timeline || planData.estimated_cost_range || 'Not specified';
  const cost = planData.estimated_cost || planData.estimated_cost_range || 'Not specified';
  const planSummary = planData.plan_summary || 'No summary available';
  const complexity = planData.estimated_complexity || 'Not specified';

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <CheckCircle2 size={20} color="#16A34A" />
        <Text style={styles.headerTitle}>AI Recommendation: HoneyDone In-House Work</Text>
      </View>

      <View style={styles.content}>
        {/* Plan Summary */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Plan Summary</Text>
          <Text style={styles.sectionText}>{planSummary}</Text>
        </View>

        {/* Key Details Grid */}
        <View style={styles.detailsGrid}>
          <View style={styles.detailItem}>
            <View style={styles.detailIcon}>
              <Clock size={16} color="#2563EB" />
            </View>
            <View style={styles.detailContent}>
              <Text style={styles.detailLabel}>Timeline</Text>
              <Text style={styles.detailValue}>{timeline}</Text>
            </View>
          </View>

          <View style={styles.detailItem}>
            <View style={styles.detailIcon}>
              <DollarSign size={16} color="#16A34A" />
            </View>
            <View style={styles.detailContent}>
              <Text style={styles.detailLabel}>Estimated Cost</Text>
              <Text style={styles.detailValue}>{cost}</Text>
            </View>
          </View>

          <View style={styles.detailItem}>
            <View style={styles.detailIcon}>
              <ClipboardList size={16} color="#F59E0B" />
            </View>
            <View style={styles.detailContent}>
              <Text style={styles.detailLabel}>Complexity</Text>
              <Text style={styles.detailValue}>{complexity}</Text>
            </View>
          </View>
        </View>

        {/* Task Breakdown */}
        {taskBreakdown.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Task Breakdown</Text>
            <View style={styles.listContainer}>
              {taskBreakdown.map((task, index) => (
                <View key={index} style={styles.listItem}>
                  <View style={styles.listBullet}>
                    <Text style={styles.listBulletText}>{index + 1}</Text>
                  </View>
                  <Text style={styles.listText}>{task}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Materials Needed */}
        {materialsNeeded.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Materials Needed</Text>
            <View style={styles.listContainer}>
              {materialsNeeded.map((material, index) => (
                <View key={index} style={styles.listItem}>
                  <Package size={16} color="#6B7280" />
                  <Text style={styles.listText}>{material}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Next Steps */}
        {planData.next_steps && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Next Steps</Text>
            <Text style={styles.sectionText}>{planData.next_steps}</Text>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#F0FDF4',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#16A34A',
    overflow: 'hidden',
    ...shadows.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  content: {
    padding: 16,
    gap: 16,
  },
  section: {
    gap: 8,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  sectionText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: '30%',
    backgroundColor: '#FFFFFF',
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  detailIcon: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailContent: {
    flex: 1,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6B7280',
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  listContainer: {
    gap: 8,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 4,
  },
  listBullet: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#16A34A',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  listBulletText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  listText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
});
